import { allMedia, movies, tvShows } from '@/mocks/media';
import { newsList } from '@/mocks/news';
import { specials } from '@/mocks/specials';
import type { MediaItem } from '@/types/media';

export const regions = ['中国', '中国香港', '中国台湾', '美国', '日本', '韩国', '英国', '法国'];

export const movieGenres = ['全部', '动作', '喜剧', '爱情', '科幻', '剧情', '悬疑', '犯罪', '冒险', '战争', '古装', '文艺', '家庭', '赛车'];

export const tvGenres = ['全部', '剧情', '科幻', '爱情', '悬疑', '古装', '武侠', '都市', '青春', '商战', '历史', '医疗', '谍战', '治愈'];

export const years = [2025, 2024, 2023, 2022, 2021];

export function getMediaById(id: string): MediaItem | undefined {
  return allMedia.find((item) => item.id === id);
}

export function getMediaByIds(ids: string[]): MediaItem[] {
  return ids
    .map((id) => allMedia.find((item) => item.id === id))
    .filter((item): item is MediaItem => Boolean(item));
}

export function getHotMedia(limit = 8, type?: 'movie' | 'tv'): MediaItem[] {
  const source = type === 'movie' ? movies : type === 'tv' ? tvShows : allMedia;
  return [...source].sort((a, b) => b.hits - a.hits).slice(0, limit);
}

export function getLatestMedia(limit = 8, type?: 'movie' | 'tv'): MediaItem[] {
  const source = type === 'movie' ? movies : type === 'tv' ? tvShows : allMedia;
  return [...source]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, limit);
}

export function getRelatedMedia(item: MediaItem, limit = 4): MediaItem[] {
  const sameType = item.type === 'movie' ? movies : tvShows;
  const scored = sameType
    .filter((m) => m.id !== item.id)
    .map((m) => ({
      item: m,
      score: m.genres.filter((g) => item.genres.includes(g)).length,
    }))
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, limit).map((s) => s.item);
}

export interface MediaFilter {
  genres: string[];
  regions: string[];
  years: number[];
  status?: string;
  sort: 'updated' | 'hits' | 'rating';
}

export function filterMedia(source: MediaItem[], filter: MediaFilter): MediaItem[] {
  let result = source.filter((item) => {
    const genreOk = filter.genres.length === 0 || item.genres.some((g) => filter.genres.includes(g));
    const regionOk = filter.regions.length === 0 || filter.regions.includes(item.region);
    const yearOk = filter.years.length === 0 || filter.years.includes(item.year);
    const statusOk = !filter.status || filter.status === 'all' || item.status === filter.status;
    return genreOk && regionOk && yearOk && statusOk;
  });

  result = [...result].sort((a, b) => {
    if (filter.sort === 'hits') return b.hits - a.hits;
    if (filter.sort === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  return result;
}

export interface SearchResult {
  media: MediaItem[];
  news: typeof newsList;
}

export function searchAll(keyword: string): SearchResult {
  const q = keyword.trim().toLowerCase();
  if (!q) return { media: [], news: [] };
  const media = allMedia.filter(
    (m) =>
      m.title.toLowerCase().includes(q) ||
      m.genres.some((g) => g.toLowerCase().includes(q)) ||
      m.cast.some((c) => c.toLowerCase().includes(q)) ||
      m.director.toLowerCase().includes(q),
  );
  const news = newsList.filter(
    (n) => n.title.toLowerCase().includes(q) || n.summary.toLowerCase().includes(q),
  );
  return { media, news };
}

export function getSpecialById(id: string) {
  return specials.find((s) => s.id === id);
}

export function getNewsById(id: string) {
  return newsList.find((n) => n.id === id);
}

export const hotKeywords = ['星辰大海', '长安十二时', '科幻', '悬疑', '法医手记', '治愈', '古装'];